import React, { useMemo, useState } from 'react';
import { Platform, View, useWindowDimensions } from 'react-native';
import { LegendList } from '@legendapp/list';

const BREAKPOINTS = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
};

const resolveColumns = (columns, width) => {
    if (typeof columns === 'number') return Math.max(1, Math.floor(columns));
    return Object.entries(columns || { xs: 1 })
        .filter(([breakpoint]) => BREAKPOINTS[breakpoint] <= width)
        .sort(([left], [right]) => BREAKPOINTS[left] - BREAKPOINTS[right])
        .reduce((value, [breakpoint, count]) => width >= BREAKPOINTS[breakpoint] ? Math.max(1, Math.floor(count)) : value, 1);
};

const resolvePadding = (padding, gap) => {
    const value = typeof padding === 'number' ? { padding } : (padding || {});
    const inset = gap / 2;
    const getValue = (side, axis) => value[`padding${side}`] ?? value[`padding${axis}`] ?? value.padding ?? 0;
    return {
        paddingTop: Math.max(0, getValue('Top', 'Vertical') - inset),
        paddingRight: Math.max(0, getValue('Right', 'Horizontal') - inset),
        paddingBottom: Math.max(0, getValue('Bottom', 'Vertical') - inset),
        paddingLeft: Math.max(0, getValue('Left', 'Horizontal') - inset),
    };
};

const CardGrid = ({
    data = [],
    renderItem,
    keyExtractor,
    columns = 1,
    gap = 16,
    padding = 16,
    estimatedItemSize = 280,
    refreshing = false,
    onRefresh,
    onEndReached,
    onEndReachedThreshold = 0.25,
    ListEmptyComponent,
    ListFooterComponent,
    ListHeaderComponent,
    contentContainerStyle,
    isFullSpan,
    getItemType,
    ...listProps
}) => {
    const { width: windowWidth } = useWindowDimensions();
    const [containerWidth, setContainerWidth] = useState(0);
    const responsiveWidth = containerWidth || windowWidth;
    const columnCount = useMemo(() => resolveColumns(columns, responsiveWidth), [columns, responsiveWidth]);
    const resolvedPadding = useMemo(() => resolvePadding(padding, gap), [gap, padding]);
    const usesRows = columnCount > 1 && typeof isFullSpan === 'function';
    const listData = useMemo(() => {
        if (!usesRows) return data;
        const rows = [];
        let items = [];
        data.forEach((item, index) => {
            if (isFullSpan(item, index)) {
                if (items.length) rows.push({ items });
                rows.push({ item, index, full: true });
                items = [];
                return;
            }
            items.push({ item, index });
            if (items.length === columnCount) {
                rows.push({ items });
                items = [];
            }
        });
        if (items.length) rows.push({ items });
        return rows;
    }, [columnCount, data, isFullSpan, usesRows]);
    const itemKey = (item, index) => keyExtractor?.(item, index) ?? item?.id ?? String(index);
    const renderGridItem = (item, index) => (
        <View key={itemKey(item, index)} style={{ flex: 1, minWidth: 0, padding: gap / 2 }}>
            {renderItem({ item, index })}
        </View>
    );

    return (
        <View
            onLayout={({ nativeEvent }) => {
                const measuredWidth = nativeEvent.layout.width;
                setContainerWidth(currentWidth => currentWidth === measuredWidth ? currentWidth : measuredWidth);
            }}
            style={{ flex: 1, minWidth: 0 }}
        >
            <LegendList
                {...listProps}
                key={`card-grid-${columnCount}-${usesRows ? 'rows' : 'columns'}`}
                recycleItems={Platform.OS !== 'web'}
                data={listData}
                keyExtractor={usesRows
                    ? (row, index) => row.full ? `full-${itemKey(row.item, row.index)}` : `row-${row.items.map(entry => itemKey(entry.item, entry.index)).join('-') || index}`
                    : keyExtractor}
                numColumns={usesRows ? 1 : columnCount}
                estimatedItemSize={estimatedItemSize}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                refreshing={refreshing}
                onRefresh={onRefresh}
                onEndReached={onEndReached}
                onEndReachedThreshold={onEndReachedThreshold}
                ListEmptyComponent={ListEmptyComponent}
                ListFooterComponent={ListFooterComponent}
                ListHeaderComponent={ListHeaderComponent}
                getItemType={usesRows
                    ? (row) => row.full ? getItemType?.(row.item) : 'cardGridRow'
                    : getItemType}
                contentContainerStyle={[resolvedPadding, contentContainerStyle]}
                renderItem={usesRows
                    ? ({ item: row }) => row.full
                        ? renderGridItem(row.item, row.index)
                        : <View style={{ flexDirection: 'row' }}>
                            {row.items.map(entry => renderGridItem(entry.item, entry.index))}
                            {Array.from({ length: columnCount - row.items.length }).map((_, index) => <View key={`empty-${index}`} style={{ flex: 1, minWidth: 0, padding: gap / 2 }} />)}
                        </View>
                    : (info) => renderGridItem(info.item, info.index)}
            />
        </View>
    );
};

export default CardGrid;
